/**
 * Curseurs Bibata (Modern, ful1e5, GPL-3.0 — voir assets/cursors/bibata/) rendus dans le
 * navigateur dans une couleur quelconque. Même méthode que scripts/generer-curseurs.py,
 * qui produit la version noire en PNG. Chromium passe à Windows des pixels prémultipliés
 * (affichage réel : couleur × alpha² + fond × (1 − alpha)) : seul un pixel sombre
 * semi-transparent s'affiche juste. D'où un fin liseré sombre lissé autour du contour blanc,
 * qui porte l'anticrénelage du bord ; les rares pixels clairs restants sont compensés pour
 * le fond de référence de chaque mode.
 */

export interface VariantesCurseur {
    light: string;
    dark: string;
}

export interface CurseursBibata {
    fleche: VariantesCurseur;
    main: VariantesCurseur;
    texte: VariantesCurseur;
}

type Rgb = readonly [number, number, number];

interface FormeBibata {
    fichier: string;
    /** Point actif en px CSS, et repli natif. */
    hotspot: string;
    repli: string;
    rotation: number;
}

const FORMES: Record<keyof CurseursBibata, FormeBibata> = {
    fleche: { fichier: 'left_ptr.svg', hotspot: '5 3', repli: 'auto', rotation: 21 },
    main: { fichier: 'hand2.svg', hotspot: '11 2', repli: 'pointer', rotation: 0 },
    texte: { fichier: 'xterm.svg', hotspot: '12 12', repli: 'text', rotation: 0 },
};

/** Mêmes valeurs que le script Python. */
const CANEVAS = 28;
const TAILLE = 24;
const POINTE = [52, 27] as const;
const FOND_SOMBRE: Rgb = [28 / 255, 34 / 255, 44 / 255];
const FOND_CLAIR: Rgb = [244 / 255, 247 / 255, 251 / 255];
/** px CSS ajoutés au trait blanc des sources, puis liseré sombre autour. */
const CONTOUR_EN_PLUS = 0.4;
const LISERE = 0.25;
const LISERE_COULEUR: Rgb = [15 / 255, 23 / 255, 42 / 255];
const LISERE_OPACITE = 0.15;
const DENSITES = [1, 1.25, 1.5, 2, 3];

interface Chemin {
    d: string;
    /** Demi-épaisseur du trait de contour, en unités de la grille 256 (0 : forme pleine). */
    trait: number;
}

interface SourceBibata {
    corps: Chemin[];
    contour: Chemin[];
}

let sources: Promise<Record<keyof CurseursBibata, SourceBibata>> | null = null;

/** Lit les SVG servis : chemins du corps (#00FF00) et du contour (#0000FF, plein ou trait). */
function chargerSources(): Promise<Record<keyof CurseursBibata, SourceBibata>> {
    sources ??= Promise.all((Object.keys(FORMES) as Array<keyof CurseursBibata>).map(async nom => {
        const texte = await fetch(`assets/cursors/bibata/${FORMES[nom].fichier}`).then(r => {
            if (!r.ok) throw new Error(`Curseur ${nom} introuvable`);
            return r.text();
        });
        const svg = new DOMParser().parseFromString(texte, 'image/svg+xml');
        const source: SourceBibata = { corps: [], contour: [] };
        svg.querySelectorAll('path').forEach(chemin => {
            const d = chemin.getAttribute('d') ?? '';
            const fill = (chemin.getAttribute('fill') ?? '').toUpperCase();
            const stroke = (chemin.getAttribute('stroke') ?? '').toUpperCase();
            if (fill === '#00FF00') source.corps.push({ d, trait: 0 });
            if (fill === '#0000FF') source.contour.push({ d, trait: 0 });
            else if (stroke === '#0000FF') source.contour.push({ d, trait: Number(chemin.getAttribute('stroke-width') ?? 0) / 2 });
        });
        return [nom, source] as const;
    })).then(entrees => Object.fromEntries(entrees) as Record<keyof CurseursBibata, SourceBibata>);
    sources.catch(() => { sources = null; });
    return sources;
}

/** Couverture (0..1) des chemins à une densité, épaissis de leur trait et de `enPlus` px CSS. */
function couverture(chemins: Chemin[], forme: FormeBibata, echelle: number, enPlus = 0): Float32Array {
    const n = Math.round(CANEVAS * echelle);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = n;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas indisponible');
    const unite = (TAILLE / 256) * echelle;
    ctx.scale(unite, unite);
    ctx.translate(POINTE[0], POINTE[1]);
    ctx.rotate((-forme.rotation * Math.PI) / 180);
    ctx.translate(-POINTE[0], -POINTE[1]);
    ctx.fillStyle = ctx.strokeStyle = '#fff';
    ctx.lineJoin = 'round';
    for (const chemin of chemins) {
        const path = new Path2D(chemin.d);
        ctx.fill(path);
        const trait = chemin.trait + enPlus * (256 / TAILLE);
        if (trait > 0) {
            ctx.lineWidth = trait * 2;
            ctx.stroke(path);
        }
    }
    const pixels = ctx.getImageData(0, 0, n, n).data;
    const resultat = new Float32Array(n * n);
    for (let i = 0; i < resultat.length; i++) resultat[i] = pixels[i * 4 + 3] / 255;
    return resultat;
}

/** Couleur et alpha droits pour que Windows affiche `p` (prémultipliée) sur le fond `fond`. */
function compenser(p: number[], a: number, fond: Rgb): [number[], number] {
    if (a >= 1) return [p, 1];
    const c = p.map(v => v / (a * a));
    if (Math.max(...c) <= 1) return [c, a];
    const cible = p.map((v, k) => v + fond[k] * (1 - a));
    const k = cible.indexOf(Math.max(...cible));
    const b = fond[k];
    const a2 = Math.min(1, (b + Math.sqrt(b * b - 4 * (b - cible[k]))) / 2);
    return [cible.map((t, i) => Math.min(1, Math.max(0, (t - fond[i] * (1 - a2)) / (a2 * a2)))), a2];
}

function image(source: SourceBibata, forme: FormeBibata, couleur: Rgb, echelle: number, sombre: boolean): string {
    const anneau = couverture(source.contour, forme, echelle, CONTOUR_EN_PLUS);
    const lisere = couverture(source.contour, forme, echelle, CONTOUR_EN_PLUS + LISERE);
    const corps = couverture(source.corps, forme, echelle);
    const n = Math.round(CANEVAS * echelle);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = n;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponible');
    const sortie = ctx.createImageData(n, n);
    for (let i = 0; i < n * n; i++) {
        const [o, r, b] = [lisere[i] * LISERE_OPACITE, anneau[i], corps[i]];
        // Idéal prémultiplié, calques superposés : liseré, contour blanc, corps coloré.
        const a = b + (1 - b) * (r + (1 - r) * o);
        if (a <= 0) continue;
        const p = couleur.map((c, k) => c * b + (1 - b) * (r + (1 - r) * LISERE_COULEUR[k] * o));
        const [c, alpha] = compenser(p, a, sombre ? FOND_SOMBRE : FOND_CLAIR);
        sortie.data.set([...c.map(v => Math.round(v * 255)), Math.round(alpha * 255)], i * 4);
    }
    ctx.putImageData(sortie, 0, 0);
    return canvas.toDataURL('image/png');
}

/** Sur fond clair, le contour blanc ne se voit pas : une couleur trop claire est assombrie. */
function lisibleSurClair(couleur: Rgb): Rgb {
    const lum = (c: Rgb) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
    let c = couleur;
    while (lum(c) > 0.45) c = c.map(v => v * 0.9) as unknown as Rgb;
    return c;
}

function variantes(source: SourceBibata, forme: FormeBibata, couleur: Rgb): VariantesCurseur {
    const valeur = (sombre: boolean) => {
        const teinte = sombre ? couleur : lisibleSurClair(couleur);
        const images = DENSITES.map(x => `url("${image(source, forme, teinte, x, sombre)}") ${x}x`).join(', ');
        return `image-set(${images}) ${forme.hotspot}, ${forme.repli}`;
    };
    return { light: valeur(false), dark: valeur(true) };
}

/** « R, G, B » (0..255) → les trois curseurs Bibata dans cette couleur, ou null si impossible. */
export async function genererCurseursBibata(rgb: string): Promise<CurseursBibata | null> {
    const parts = rgb.split(/[\s,]+/).filter(Boolean).map(Number);
    if (parts.length < 3 || parts.some(v => Number.isNaN(v))) return null;
    const couleur = parts.slice(0, 3).map(v => Math.min(255, Math.max(0, v)) / 255) as unknown as Rgb;
    try {
        const s = await chargerSources();
        return {
            fleche: variantes(s.fleche, FORMES.fleche, couleur),
            main: variantes(s.main, FORMES.main, couleur),
            texte: variantes(s.texte, FORMES.texte, couleur),
        };
    } catch {
        return null;
    }
}
